import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Radio, RefreshCw, AlertTriangle, Bus, Clock, Play, Square } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GtfsRtPage = () => {
  const { projectId } = useParams();
  const [feedUrl, setFeedUrl] = useState('');
  const [feedType, setFeedType] = useState('vehicle_positions');
  const [feedData, setFeedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(15);
  const [center, setCenter] = useState([43.26, -2.93]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (autoRefresh && feedUrl) {
      intervalRef.current = setInterval(() => {
        fetchFeed();
      }, refreshInterval * 1000);
      return () => clearInterval(intervalRef.current);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [autoRefresh, feedUrl, refreshInterval]);

  const fetchFeed = async () => {
    if (!feedUrl) {
      toast.error('Introduce una URL de feed GTFS-RT');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/projects/${projectId}/gtfs-rt/fetch`, {
        url: feedUrl,
        feed_type: feedType,
      });
      setFeedData(res.data);
      setLastUpdate(new Date());

      // Center map on first vehicle position
      const vehicles = res.data.entities.filter(e => e.type === 'vehicle_position');
      if (vehicles.length > 0) {
        const avgLat = vehicles.reduce((s, v) => s + v.position.latitude, 0) / vehicles.length;
        const avgLon = vehicles.reduce((s, v) => s + v.position.longitude, 0) / vehicles.length;
        setCenter([avgLat, avgLon]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al obtener feed GTFS-RT');
    } finally {
      setLoading(false);
    }
  };

  const vehicles = feedData?.entities?.filter(e => e.type === 'vehicle_position') || [];
  const tripUpdates = feedData?.entities?.filter(e => e.type === 'trip_update') || [];
  const alerts = feedData?.entities?.filter(e => e.type === 'service_alert') || [];

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-96 bg-white border-r flex flex-col">
        <div className="p-4 border-b flex-shrink-0 space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2" data-testid="gtfsrt-title">
            <Radio className="w-5 h-5 text-green-600" />
            GTFS Realtime
          </h2>

          <div className="space-y-2">
            <Label>URL del Feed</Label>
            <Input
              data-testid="gtfsrt-url-input"
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              placeholder="https://api.example.com/gtfs-rt/vehiclePositions"
            />
          </div>

          <div className="space-y-2">
            <Label>Tipo de Feed</Label>
            <select
              data-testid="gtfsrt-type-select"
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
              value={feedType}
              onChange={(e) => setFeedType(e.target.value)}
            >
              <option value="vehicle_positions">Vehicle Positions</option>
              <option value="trip_updates">Trip Updates</option>
              <option value="service_alerts">Service Alerts</option>
            </select>
          </div>

          <div className="flex gap-2">
            <Button
              data-testid="gtfsrt-fetch-button"
              onClick={fetchFeed}
              disabled={loading || !feedUrl}
              className="flex-1"
            >
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {loading ? 'Cargando...' : 'Obtener Feed'}
            </Button>
          </div>

          {/* Auto-refresh controls */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              Auto-refresh
            </label>
            <Input
              type="number"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Math.max(5, parseInt(e.target.value) || 15))}
              className="w-20"
              min={5}
            />
            <span className="text-xs text-slate-500">seg</span>
          </div>

          {lastUpdate && (
            <p className="text-xs text-slate-500">
              Última actualización: {lastUpdate.toLocaleTimeString()}
            </p>
          )}

          {/* Stats */}
          {feedData && (
            <div className="grid grid-cols-3 gap-2">
              <Card className="p-2 text-center">
                <Bus className="w-4 h-4 mx-auto text-blue-600" />
                <p className="text-lg font-bold">{vehicles.length}</p>
                <p className="text-xs text-slate-500">Vehículos</p>
              </Card>
              <Card className="p-2 text-center">
                <Clock className="w-4 h-4 mx-auto text-orange-600" />
                <p className="text-lg font-bold">{tripUpdates.length}</p>
                <p className="text-xs text-slate-500">Trip Updates</p>
              </Card>
              <Card className="p-2 text-center">
                <AlertTriangle className="w-4 h-4 mx-auto text-red-600" />
                <p className="text-lg font-bold">{alerts.length}</p>
                <p className="text-xs text-slate-500">Alertas</p>
              </Card>
            </div>
          )}
        </div>

        {/* Entity list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {vehicles.map((v, idx) => (
            <Card key={idx} className="p-2 text-xs">
              <div className="flex items-center gap-2">
                <Bus className="w-3 h-3 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{v.vehicle?.label || v.vehicle?.id || `Vehículo ${idx+1}`}</p>
                  <p className="text-slate-500">Ruta: {v.trip?.route_id || '?'} | Trip: {v.trip?.trip_id || '?'}</p>
                  {v.position?.speed != null && <p className="text-slate-500">Vel: {(v.position.speed * 3.6).toFixed(0)} km/h</p>}
                </div>
              </div>
            </Card>
          ))}
          {tripUpdates.map((tu, idx) => (
            <Card key={`tu-${idx}`} className="p-2 text-xs">
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3 text-orange-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">Trip: {tu.trip?.trip_id}</p>
                  <p className="text-slate-500">Ruta: {tu.trip?.route_id} | Retraso: {tu.delay != null ? `${tu.delay}s` : 'N/A'}</p>
                  <p className="text-slate-500">{tu.stop_time_updates?.length || 0} paradas con actualización</p>
                </div>
              </div>
            </Card>
          ))}
          {alerts.map((a, idx) => (
            <Card key={`a-${idx}`} className="p-2 text-xs border-red-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{a.header_text || 'Alerta de servicio'}</p>
                  <p className="text-slate-500 truncate">{a.description_text || ''}</p>
                </div>
              </div>
            </Card>
          ))}
          {feedData && feedData.entities.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">No se encontraron entidades en el feed</p>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative" data-testid="gtfsrt-map-container">
        <MapContainer
          center={center}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapCenterUpdater center={center} />

          {vehicles.map((v, idx) => (
            <CircleMarker
              key={`v-${idx}`}
              center={[v.position.latitude, v.position.longitude]}
              radius={8}
              pathOptions={{
                color: '#1d4ed8',
                fillColor: '#3b82f6',
                fillOpacity: 0.9,
                weight: 2,
              }}
            >
              <Popup>
                <div className="p-2 text-sm">
                  <p className="font-semibold">{v.vehicle?.label || v.vehicle?.id || 'Vehículo'}</p>
                  <p className="text-xs text-slate-600">Ruta: {v.trip?.route_id || '?'}</p>
                  <p className="text-xs text-slate-600">Trip: {v.trip?.trip_id || '?'}</p>
                  {v.position?.speed != null && (
                    <p className="text-xs text-slate-600">Velocidad: {(v.position.speed * 3.6).toFixed(1)} km/h</p>
                  )}
                  {v.position?.bearing != null && (
                    <p className="text-xs text-slate-600">Rumbo: {v.position.bearing.toFixed(0)}°</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    {v.position.latitude.toFixed(5)}, {v.position.longitude.toFixed(5)}
                  </p>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>

        {/* Legend */}
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
          <h4 className="text-xs font-semibold mb-1">GTFS-RT en vivo</h4>
          {autoRefresh && (
            <div className="flex items-center gap-1 text-xs text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Actualizando cada {refreshInterval}s
            </div>
          )}
          <div className="flex items-center gap-1 text-xs mt-1">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <span>{vehicles.length} vehículos</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center]);
  return null;
};

export default GtfsRtPage;
